import React from 'react';
import PropTypes from 'prop-types';
import { Bling as GPT } from 'react-gpt';
import Sticky from 'react-stickynode';
import MediaQuery from 'react-responsive';
import { logger } from 'packages';
import rand from 'static/script/utils/almost-random';
import Messaging from 'static/script/packages/react/messaging';
import AdComponent from './ad-component';
import adStates from './ad-states';

const REFRESH_INTERVAL_MS = 60000; // 60 seconds is minimum recommended by google

class DfpAd extends React.Component {
    static propTypes = {
        adContainerClass: PropTypes.string,
        adUnitPath: PropTypes.string.isRequired,
        autoscale: PropTypes.bool,
        autoscaleOffsetWidth: PropTypes.number,
        className: PropTypes.string,
        lazyLoad: PropTypes.bool,
        maxViewportWidth: PropTypes.number,
        minViewportWidth: PropTypes.number.isRequired,
        page: PropTypes.object,
        pageTargeting: PropTypes.object,
        refresh: PropTypes.bool,
        sizeMapping: PropTypes.array,
        slotTargeting: PropTypes.object,
        stickyBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        stickyEnabled: PropTypes.bool,
        stickyTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    };

    static defaultProps = {
        adContainerClass: '',
        autoscale: false, // true => ads that don't fit in the current adslot will be autoscaled to fit
        autoscaleOffsetWidth: 0,
        lazyLoad: false,
        refresh: false,
        stickyEnabled: false
    };

    constructor(props) {
        super(props);
        const {
            sizeMapping,
            autoscale,
            autoscaleOffsetWidth,
            slotTargeting
        } = props;

        const expectedAdWidth = sizeMapping[0].slot[0][0];
        const expectedAdHeight = sizeMapping[0].slot[0][1];
        const initialScale =
            autoscale &&
            autoscaleOffsetWidth > 0 &&
            expectedAdWidth > 0 &&
            autoscaleOffsetWidth < expectedAdWidth
                ? autoscaleOffsetWidth / expectedAdWidth
                : 1;

        this.state = {
            adState: adStates.ready,
            scale: initialScale,
            width: expectedAdWidth,
            height: expectedAdHeight
        };
        this.id = `bling-ad-${rand.get()}`;
        this.position = slotTargeting.pos;
        this.googleIframeId = null;
        this.messages = new Messaging();
        this.componentRef = React.createRef();
    }

    handleCollapseEvent = (evt, passbackIframeId) => {
        // Collapse can only occur when when adslot is filled
        if (this.state.adState !== adStates.filled || !passbackIframeId) {
            return;
        }

        if (this.googleIframeId === null) {
            let el = document.getElementById(this.id);
            if (!el) {
                return;
            }

            try {
                let iframeIds = el.getElementsByTagName('iframe');
                if (!iframeIds || iframeIds.length === 0) {
                    return;
                }

                this.googleIframeId = iframeIds[0].id;
            } catch (err) {
                logger(err);
            }
        }

        if (passbackIframeId === this.googleIframeId) {
            this.setState({
                width: 0,
                height: 0,
                adState: adStates.empty
            });
        }
    };

    getTargeting = () => {
        let targeting = {
            ...this.props.slotTargeting,
            ...this.props.pageTargeting
        };

        if ('page' in this.props) {
            targeting.pageUrl = this.props.page.url;
            targeting.page = this.props.page.pageName;
        }
        return targeting;
    };

    clearRefreshTimer = () => {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    };

    /**
     * refreshes google publisher api slot if document is not hidden, clears refresh timer regardless
     * @param {googletag.Slot} slot
     * @returns {void}
     */
    refreshSlot = slot => {
        if (!slot || !GPT) {
            return;
        }

        // verify document is not hidden
        if (document && document.hidden === false) {
            GPT.refresh([slot]);
        }
    };

    /**
     * enables refresh timer if visibility api is available, clears lingering refresh timer
     * @param {googletag.events.Event} evt
     * @returns {void}
     */
    startRefreshTimer = evt => {
        this.clearRefreshTimer();

        // guard event data
        if (!(evt && evt.slot)) {
            return;
        }

        // check support for visibility api
        if (document && typeof document.hidden !== 'undefined') {
            this.refreshTimer = setTimeout(
                this.refreshSlot,
                REFRESH_INTERVAL_MS,
                evt.slot
            );
        }
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        // when url changes refresh ads
        if (
            'page' in this.props &&
            this.props.page.url !== nextProps.page.url
        ) {
            this.setState({ adState: adStates.ready });
        }
    }

    componentDidMount() {
        this.messages.subscribe(
            ['third-party:ads:collapse'],
            this.handleCollapseEvent
        );
    }

    componentWillUnmount() {
        this.clearRefreshTimer();
        this.messages.teardown();
    }

    onMediaQueryChange = () => {
        this.setState({ adState: adStates.ready });
    };

    onSlotRenderEnded = ev => {
        if (ev.isEmpty) {
            this.setState({
                adState: adStates.empty,
                width: 0,
                height: 0
            });
        } else {
            const width = ev.size[0];
            const height = ev.size[1];
            const scale = this.getScaleToFitContainer(width);
            this.setState({
                adState: adStates.filled,
                width,
                height,
                scale
            });
        }
    };

    /**
     * calculate how much smaller the ad width needs to be to fit it's container
     * @param {number} width
     * @returns {number} resulting scale value
     */
    getScaleToFitContainer(width) {
        const { autoscale } = this.props;
        if (autoscale && this.componentRef.current !== null) {
            const availableWidth = this.componentRef.current.offsetWidth;
            if (availableWidth && width && availableWidth < width) {
                return availableWidth / width;
            }
        }
        return 1;
    }

    render() {
        const {
            adContainerClass,
            adUnitPath,
            className,
            lazyLoad,
            maxViewportWidth,
            minViewportWidth,
            refresh,
            sizeMapping,
            stickyBottom,
            stickyEnabled,
            stickyTop
        } = this.props;

        const { adState, width, height, scale } = this.state;
        let mediaQueryProps = { minWidth: minViewportWidth };
        if (maxViewportWidth) {
            mediaQueryProps.maxWidth = maxViewportWidth;
        }

        let adComponent = (
            <AdComponent
                adState={adState}
                width={width}
                height={height}
                scale={scale}
                className={className}>
                <GPT
                    adUnitPath={adUnitPath}
                    targeting={this.getTargeting()}
                    collapseEmptyDiv={true}
                    renderWhenViewable={lazyLoad}
                    viewableThreshold={0.01}
                    sizeMapping={sizeMapping}
                    id={this.id}
                    onSlotRenderEnded={this.onSlotRenderEnded}
                    onMediaQueryChange={this.onMediaQueryChange}
                    {...refresh === true && {
                        onImpressionViewable: this.startRefreshTimer
                    }}
                />
            </AdComponent>
        );

        if (stickyEnabled === true) {
            adComponent = (
                <Sticky
                    enabled={stickyEnabled}
                    top={stickyTop}
                    bottomBoundary={stickyBottom}
                    innerZ={1}>
                    {adComponent}
                </Sticky>
            );
        }

        return (
            <MediaQuery {...mediaQueryProps}>
                <div className={adContainerClass} ref={this.componentRef}>
                    {adComponent}
                </div>
            </MediaQuery>
        );
    }
}

export default DfpAd;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/Areas/MyTradera/static/script/app/components/ads/dfp-ad.jsx